import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Paper,
  Chip
} from '@mui/material';

const cableTypes = [
  {
    id: 'audio',
    name: 'Audio Cable',
    description: 'Professional audio cables for microphones, instruments, and speakers',
    image: '/images/musicalnote.jpg'
  },
  {
    id: 'power',
    name: 'Power Cable',
    description: 'Power distribution and electrical cables for various applications',
    image: '/images/lightningbolt.jpg'
  },
  {
    id: 'dmx',
    name: 'DMX Cable',
    description: 'DMX lighting control cables for professional stage and studio applications',
    image: '/images/dmxlight.jpg'
  },
  {
    id: 'hybrid',
    name: 'Hybrid Cable',
    description: 'Custom hybrid cables for specialized applications combining multiple functions',
    image: '/images/musicalbolt.jpg'
  }
];

function CableTypeSelection({ onSelect, onReady, onSelectionChange }) {
  const [selectedType, setSelectedType] = React.useState(null);

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    
    // Immediately advance based on cable type
    if (type === 'power' || type === 'dmx') {
      // Auto-advance Power and DMX cables with 'end-to-end' style
      onSelect({ 
        type: type, 
        style: 'end-to-end'
      });
    } else if (type === 'hybrid') {
      // Auto-advance Hybrid cables (no signal type selection needed)
      onSelect({ 
        type: type
      });
    } else {
      onSelect({ 
        type: type
      });
    }
  };



  React.useEffect(() => {
    const isReady = !!selectedType;
    if (typeof onReady === 'function') {
      onReady(isReady);
    }
    if (typeof onSelectionChange === 'function') {
      onSelectionChange(isReady);
    }
  }, [selectedType, onReady, onSelectionChange]);

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h5" gutterBottom>
        Build Your Custom Cable
      </Typography>
      <Typography 
        variant="body1" 
        sx={{ 
          color: 'rgba(0, 0, 0, 0.8)',
          marginBottom: 2
        }} 
        paragraph
      >
        Choose your cable type to get started with professional-grade connectors.
      </Typography>

      {/* Cable Type Selection */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Select your cable type
        </Typography>
        <Grid container spacing={3}>
          {cableTypes.map((type) => (
            <Grid item xs={12} sm={6} md={3} key={type.id}>
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  border: selectedType === type.id ? 2 : 0,
                  borderColor: 'primary.main',
                  '&:hover': {
                    boxShadow: 6
                  }
                }}
                onClick={() => handleTypeSelect(type.id)}
              >
                <CardMedia
                  component="img"
                  height="120"
                  image={type.image}
                  alt={type.name}
                  sx={{
                    objectFit: 'contain',
                    backgroundColor: 'grey.200'
                  }}
                />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography gutterBottom variant="h6" component="h2">
                    {type.name}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'rgba(0, 0, 0, 0.7)',
                      lineHeight: 1.4
                    }}
                  >
                    {type.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>



      {/* Selection Summary */}
      {selectedType && (
        <Paper elevation={1} sx={{ p: 2, backgroundColor: 'grey.50' }}>
          <Typography variant="subtitle1" gutterBottom>
            <strong>Your Selection:</strong>
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Chip 
              label={`Type: ${cableTypes.find(t => t.id === selectedType)?.name}`}
              color="secondary"
              size="small"
            />
          </Box>
        </Paper>
      )}
    </Box>
  );
}

export { cableTypes };
export default CableTypeSelection; 